import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, X, PartyPopper } from 'lucide-react';
import { Button } from './ui/button';
import { useEffect, useState } from 'react';

const WinnerModal = ({ winner, onClose }) => {
  const [confetti, setConfetti] = useState([]);

  useEffect(() => {
    if (winner) {
      // Generate confetti pieces
      const pieces = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 2,
        duration: 2 + Math.random() * 2,
        color: ['#EAB308', '#A855F7', '#06b6d4', '#f43f5e'][Math.floor(Math.random() * 4)]
      }));
      setConfetti(pieces);
    }
  }, [winner]);

  return (
    <AnimatePresence>
      {winner && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/90 p-4 backdrop-blur-xl sm:p-6"
          data-testid="winner-modal"
        >
          {/* Confetti */}
          {confetti.map((piece) => (
            <div
              key={piece.id}
              className="absolute w-3 h-3 confetti-piece"
              style={{
                left: `${piece.left}%`,
                top: '-20px',
                backgroundColor: piece.color,
                animationDelay: `${piece.delay}s`,
                animationDuration: `${piece.duration}s`
              }}
            />
          ))}

          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 20 }}
            className="relative w-full max-w-2xl space-y-5 rounded-3xl border-2 border-[#EAB308] bg-[#18181b] p-6 text-center sm:space-y-6 sm:p-12"
          >
            <button
              onClick={onClose}
              data-testid="close-winner-modal-btn"
              className="absolute top-4 right-4 text-[#a1a1aa] hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 10, -10, 0]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatDelay: 1
              }}
              className="flex justify-center"
            >
              <div className="rounded-3xl bg-[#EAB308]/20 p-5 sm:p-8">
                <Trophy className="h-16 w-16 text-[#EAB308] sm:h-24 sm:w-24" />
              </div>
            </motion.div>

            <div>
              <h2 className="mb-3 font-heading text-4xl font-bold sm:text-6xl">
                <span className="gold-gradient">YOU WON!</span>
              </h2>
              <p className="mb-2 break-words text-lg font-semibold text-white sm:text-2xl">
                {winner.draw?.title}
              </p>
              <p className="text-base text-[#a1a1aa] sm:text-lg">
                Winning Ticket: <span className="font-heading text-xl font-bold text-[#EAB308] sm:text-2xl">#{winner.draw?.winning_ticket}</span>
              </p>
            </div>

            <div className="bg-[#27272a] rounded-2xl p-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <PartyPopper className="w-5 h-5 text-[#A855F7]" />
                <h3 className="text-xl font-heading font-bold text-white">Your Prize</h3>
              </div>
              <p className="break-words text-xl font-bold text-[#EAB308] sm:text-2xl">
                {winner.draw?.prize_description}
              </p>
            </div>

            <div className="pt-2 sm:pt-6">
              <p className="mb-4 text-sm text-[#a1a1aa] sm:text-base">
                Congratulations! You'll receive instructions via WhatsApp on how to claim your prize.
              </p>
              <Button
                onClick={onClose}
                data-testid="claim-prize-btn"
                className="h-12 w-full animate-pulse rounded-full bg-[#EAB308] px-8 text-base font-bold text-black hover:bg-[#CA8A04] sm:h-14 sm:w-auto sm:text-lg"
              >
                Awesome!
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WinnerModal;
